import React, { useContext, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useQuery } from "react-query";

import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import { getSuppliers } from "../../../api/supplierApi";
import { LanguageContext } from "../../../components/LanguageContext";

const Empty = () => (
    <Typography variant="body2" color="text.disabled">
        —
    </Typography>
);

const SupplierPage = () => {
    const { translations } = useContext(LanguageContext) || {};

    const { data, isLoading } = useQuery({
        queryKey: ["suppliers"],
        queryFn: () => getSuppliers(),
        staleTime: 5 * 60 * 1000,
    });

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminSupplierPageID || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "code",
                headerName: translations?.adminSupplierPageCode || "Šifra",
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "name",
                headerName: translations?.adminSupplierPageName || "Naziv",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={(theme) => ({
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(15,23,42,0.04)",
                                color: "primary.main",
                            })}
                        >
                            <LocalShippingOutlinedIcon fontSize="small" />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={params.value}
                        >
                            {params.value}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: "min_value",
                headerName:
                    translations?.adminSupplierPageMinValue || "Min. vrednost",
                minWidth: 150,
                flex: 0.8,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.row.min_value != null ? (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <EuroOutlinedIcon
                                sx={{ fontSize: "1rem", color: "text.secondary" }}
                            />
                            <Typography
                                sx={{ fontFamily: "monospace", fontWeight: 600 }}
                            >
                                {params.row.min_value}
                            </Typography>
                        </Stack>
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "automatic_ordering",
                headerName:
                    translations?.automaticOrdering || "Automatsko naručivanje",
                minWidth: 200,
                flex: 1,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    const value = params.row.automatic_ordering;
                    if (value == null) return <Empty />;
                    const isEnabled = value === true || value === 1;
                    return (
                        <StatusBadge
                            status={isEnabled ? "active" : "inactive"}
                            label={
                                isEnabled
                                    ? translations?.tableLangBooleanCellTrueLabel ||
                                      "Da"
                                    : translations?.tableLangBooleanCellFalseLabel ||
                                      "Ne"
                            }
                        />
                    );
                },
            },
            {
                field: "frequency",
                headerName: translations?.frequency || "Frekvencija",
                minWidth: 140,
                flex: 0.8,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.row.frequency != null ? (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <RepeatRoundedIcon
                                sx={{ fontSize: "1rem", color: "text.secondary" }}
                            />
                            <Typography sx={{ fontWeight: 600 }}>
                                {params.row.frequency}
                            </Typography>
                        </Stack>
                    ) : (
                        <Empty />
                    ),
            },
            {
                field: "order_day",
                headerName: translations?.orderDay || "Dan naručivanja",
                minWidth: 160,
                flex: 0.8,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.row.order_day != null ? (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CalendarTodayOutlinedIcon
                                sx={{ fontSize: "1rem", color: "text.secondary" }}
                            />
                            <Typography sx={{ fontWeight: 600 }}>
                                {params.row.order_day}
                            </Typography>
                        </Stack>
                    ) : (
                        <Empty />
                    ),
            },
        ],
        [translations]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<LocalShippingOutlinedIcon />}
                eyebrow={
                    translations?.adminAdministratorMenuSuppliers || "Suppliers"
                }
                title={translations?.adminSupplierPageName || "Dobavljači"}
                description={
                    translations?.suppliersSubtitle ||
                    "Pregled dobavljača sa pravilima naručivanja."
                }
            />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    emptyIcon={<LocalShippingOutlinedIcon />}
                    emptyTitle={translations?.noSuppliersTitle || "Nema dobavljača"}
                    emptyDescription={
                        translations?.noSuppliersDescription ||
                        "Sinhronizujte dobavljače iz Navigatora."
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default SupplierPage;